/**
 * Governed cinema footprint reapproval — the operator path out of
 * NEEDS_REAPPROVAL.
 *
 * This file is byte-identical between the Cortex (client) and Quantum
 * (superadmin) portals (asserted by CinemaReapprovalDirective.parity.test.js)
 * because both portals must state the same approval authority, the same
 * acknowledged footprint version, and the same consequence of approving.
 *
 * A footprint mutation on an APPROVED campaign moves it to NEEDS_REAPPROVAL and
 * blocks deployment. The backend decides — from the caller's own credentials —
 * whether that caller may perform the reapproval; this module only renders that
 * decision. It never infers authority locally, never assumes the block can be
 * cleared, and never fabricates a footprint version to acknowledge. When the
 * backend does not hand back a complete, actionable directive, the surface
 * degrades to naming who holds the authority rather than offering an action
 * that would fail.
 */

export const CINEMA_REAPPROVAL_SCHEMA_VERSION = "cinema_footprint_reapproval_v1";

// What the operator is offered.
//   ACTION   — this viewer holds approval authority; perform the governed flow.
//   DEEP_LINK— this viewer cannot approve here, but this portal can route to
//              the canonical approval surface.
//   REFERRAL — approval lives in a portal this viewer cannot reach; name the
//              authority honestly instead of linking nowhere.
//   NONE     — nothing to approve.
export const CINEMA_REAPPROVAL_CTA_MODES = {
  ACTION: "ACTION",
  DEEP_LINK: "DEEP_LINK",
  REFERRAL: "REFERRAL",
  NONE: "NONE",
};

const positiveInteger = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

const text = (value) => String(value ?? "").trim();

const readRole = (value) => text(value).toLowerCase().replace(/[_\s]/g, "");

/**
 * Tolerant read of the backend `reapproval` directive. Absent evidence is
 * absent — a missing directive is never upgraded into "approval available".
 */
export function normalizeCinemaReapprovalDirective(source) {
  if (!source || typeof source !== "object") return null;
  const action = source.action && typeof source.action === "object" ? source.action : null;
  const actionBody =
    action && action.body && typeof action.body === "object" ? action.body : null;
  const surface =
    source.canonicalApprovalSurface && typeof source.canonicalApprovalSurface === "object"
      ? source.canonicalApprovalSurface
      : source.canonical_approval_surface && typeof source.canonical_approval_surface === "object"
        ? source.canonical_approval_surface
        : null;

  const campaignId = positiveInteger(source.campaignId ?? source.campaign_id);
  const titleContextId = positiveInteger(source.titleContextId ?? source.title_context_id);
  const footprintVersion = positiveInteger(source.footprintVersion ?? source.footprint_version);

  return {
    schemaVersion: text(source.schemaVersion ?? source.schema_version) || null,
    required: (source.required ?? source.requires_reapproval) === true,
    reason: text(source.reason) || null,
    campaignId,
    titleContextId,
    footprintVersion,
    currentStatus: text(source.currentStatus ?? source.current_status).toUpperCase() || null,
    resultingStatus: text(source.resultingStatus ?? source.resulting_status).toUpperCase() || null,
    authorizedRole: text(source.authorizedRole ?? source.authorized_role) || null,
    actorAuthorized: (source.actorAuthorized ?? source.actor_authorized) === true,
    actionable: (source.actionable ?? false) === true,
    action: action
      ? {
          method: text(action.method).toUpperCase() || "POST",
          path: text(action.path) || null,
          body: actionBody
            ? {
                campaignId: positiveInteger(actionBody.campaignId ?? actionBody.campaign_id),
                titleContextId: positiveInteger(
                  actionBody.titleContextId ?? actionBody.title_context_id
                ),
                footprintVersion: positiveInteger(
                  actionBody.footprintVersion ?? actionBody.footprint_version
                ),
              }
            : null,
        }
      : null,
    canonicalApprovalSurface: surface
      ? {
          portal: text(surface.portal).toUpperCase() || null,
          label: text(surface.label) || null,
          path: text(surface.path) || null,
          campaignId: positiveInteger(surface.campaignId ?? surface.campaign_id),
        }
      : null,
  };
}

const isCompleteAcknowledgement = (body) =>
  Boolean(body && body.campaignId && body.titleContextId && body.footprintVersion);

/**
 * Presentation model for the governance CTA.
 *
 * `portalCanReachApprovalSurface` is the caller's honest statement about
 * whether THIS portal can route to the canonical approval surface. Cortex
 * cannot open a Quantum-only route, so it passes false and the panel names the
 * authority instead of offering a link that goes nowhere.
 */
export function buildCinemaReapprovalCta({
  directive,
  portalCanReachApprovalSurface = false,
  submitting = false,
  confirming = false,
} = {}) {
  const normalized =
    directive && directive.schemaVersion !== undefined
      ? directive
      : normalizeCinemaReapprovalDirective(directive);

  if (!normalized || !normalized.required) {
    return {
      visible: false,
      mode: CINEMA_REAPPROVAL_CTA_MODES.NONE,
      headline: null,
      body: null,
      ctaLabel: null,
      confirmLabel: null,
      confirmPrompt: null,
      cancelLabel: null,
      deepLinkPath: null,
      authorityNote: null,
      requestBody: null,
      submitting: false,
      confirming: false,
    };
  }

  const version = normalized.footprintVersion;
  const versionLabel = version ? `Footprint V${version}` : "the revised footprint";
  const acknowledgement = normalized.action?.body || null;
  const canAct =
    normalized.actionable &&
    normalized.actorAuthorized &&
    Boolean(normalized.action?.path) &&
    isCompleteAcknowledgement(acknowledgement);

  const surface = normalized.canonicalApprovalSurface;
  const canDeepLink =
    !canAct && portalCanReachApprovalSurface === true && Boolean(surface?.path);

  const mode = canAct
    ? CINEMA_REAPPROVAL_CTA_MODES.ACTION
    : canDeepLink
      ? CINEMA_REAPPROVAL_CTA_MODES.DEEP_LINK
      : CINEMA_REAPPROVAL_CTA_MODES.REFERRAL;

  const authorityNote = normalized.authorizedRole
    ? `Approval authority: ${normalized.authorizedRole}.`
    : null;

  return {
    visible: true,
    mode,
    headline: "Revised footprint awaiting reapproval",
    body:
      normalized.reason ||
      "The governed cinema footprint changed after approval; the revised targeting must be reviewed and reapproved before deployment.",
    // The label states what the operator is approving. It never implies the
    // system already judged the change acceptable.
    ctaLabel:
      mode === CINEMA_REAPPROVAL_CTA_MODES.ACTION
        ? `Review & Reapprove ${versionLabel}`
        : mode === CINEMA_REAPPROVAL_CTA_MODES.DEEP_LINK
          ? "Open approval surface"
          : null,
    confirmLabel: `Approve ${versionLabel}`,
    confirmPrompt:
      mode === CINEMA_REAPPROVAL_CTA_MODES.ACTION
        ? `Confirm that you have reviewed the revised targeting. Approving records ${versionLabel} as the governed footprint and returns the campaign to ${
            normalized.resultingStatus || "APPROVED"
          }.`
        : null,
    cancelLabel: "Cancel",
    deepLinkPath: canDeepLink ? surface.path : null,
    authorityNote:
      mode === CINEMA_REAPPROVAL_CTA_MODES.ACTION
        ? authorityNote
        : [
            authorityNote,
            surface?.label
              ? `Reapproval is performed on ${surface.label}${
                  surface.portal ? ` (${surface.portal})` : ""
                }.`
              : null,
          ]
            .filter(Boolean)
            .join(" ") || null,
    // Sent verbatim: the version the operator saw IS the version approved.
    requestBody: canAct ? { ...acknowledgement } : null,
    actionPath: canAct ? normalized.action.path : null,
    submitting: submitting === true,
    confirming: confirming === true,
  };
}

/**
 * A viewer's own role is only ever a hint for optimistic rendering — the
 * backend directive remains the authority. Exported so both portals classify
 * roles identically when they have nothing else to go on.
 */
export function isCinemaReapprovalAuthorityRole(role) {
  return readRole(role) === "superadmin";
}
